/**
 * BillTrendChart — andamento delle ultime N bollette.
 *
 * Replica web del componente mobile
 * `enermed-app/src/screens/invoices/components/v2/BillTrendChart.js`:
 *   - prende le ultime 12 bollette in ordine cronologico crescente
 *   - mostra una linea cyan con area sotto (gradient)
 *   - tooltip su hover: "MAR 24: 123,45 €"
 *   - placeholder se 0 o 1 bollette (servono ≥ 2 punti per un trend)
 */

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { parseBackendDate } from '@/lib/dates'
import type { Bolletta } from '@/lib/graphql/types'

const MAX_BILLS = 12

type Point = { mese: string; importo: number }

function buildPoints(invoices: Bolletta[] | undefined): Point[] {
  if (!invoices?.length) return []
  return [...invoices]
    .filter((b) => typeof b?.imponibile === 'number')
    .slice(0, MAX_BILLS)
    .reverse()
    .map((b) => {
      const dataRef = b.data_emissione || b.al || b.dal
      const parsed = parseBackendDate(dataRef)
      const mese = parsed
        ? `${parsed.format('MMM').toUpperCase()} ${parsed.format('YY')}`
        : (b.codice ?? '').slice(0, 7)
      return {
        mese,
        importo: Number(b.imponibile.toFixed(2)),
      }
    })
}

function formatEuroShort(v: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v)
}

export function BillTrendChart({ invoices }: { invoices: Bolletta[] | undefined }) {
  const data = buildPoints(invoices)

  if (data.length < 2) {
    const single = data[0]
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
        {single && (
          <p className="text-xl font-bold text-secondary">
            {Number(single.importo).toFixed(2).replace('.', ',')} €
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          Servono almeno 2 bollette per visualizzare l’andamento.
        </p>
      </div>
    )
  }

  return (
    <div className="h-[220px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
          <defs>
            <linearGradient id="bill-trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity={0.45} />
              <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            vertical={false}
            strokeDasharray="3 3"
            stroke="hsl(var(--border))"
          />
          <XAxis
            dataKey="mese"
            tickLine={false}
            axisLine={false}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            tickFormatter={(v: number) => formatEuroShort(v)}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1 }}
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
            formatter={(value) => [
              `${Number(value).toFixed(2).replace('.', ',')} €`,
              'Importo',
            ]}
          />
          <Area
            type="monotone"
            dataKey="importo"
            stroke="hsl(var(--secondary))"
            strokeWidth={2.5}
            fill="url(#bill-trend-fill)"
            dot={{
              r: 3,
              fill: 'hsl(var(--secondary))',
              stroke: 'hsl(var(--card))',
              strokeWidth: 1.5,
            }}
            activeDot={{
              r: 5,
              fill: 'hsl(var(--secondary))',
              stroke: 'hsl(var(--card))',
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
